<template>
  <div class="landing-page">
    <header class="landing-header">
      <div class="header-inner">
        <a href="#top" class="header-brand" aria-label="RabiiTrip ホームへ">
          <img src="/favicon.png" alt="" class="header-logo" />
          <span>RabiiTrip</span>
        </a>
        <nav class="header-nav desktop-nav" aria-label="メインナビゲーション">
          <a href="#features">機能</a>
          <a href="#faq">よくある質問</a>
          <router-link to="/trial" class="header-trial">試してみる</router-link>
          <router-link to="/login" class="header-login">ログイン</router-link>
        </nav>
        <button
          type="button"
          class="mobile-menu-toggle-btn"
          aria-label="メニューを開く"
          @click="isMobileMenuOpen = !isMobileMenuOpen"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      </div>

      <!-- モバイル用メニュー展開 -->
      <transition name="drawer-fade">
        <div v-if="isMobileMenuOpen" class="mobile-landing-nav">
          <a href="#features" @click="isMobileMenuOpen = false">機能</a>
          <a href="#faq" @click="isMobileMenuOpen = false">よくある質問</a>
          <router-link to="/trial" class="btn btn-secondary w-full" @click="isMobileMenuOpen = false">みんなで計画を始める</router-link>
          <router-link to="/login" class="btn btn-primary w-full" @click="isMobileMenuOpen = false">ログイン</router-link>
        </div>
      </transition>
    </header>
    <section id="top" class="hero-section">
      <div class="hero-content">
        <div class="hero-copy">
          <p class="hero-kicker">グループの旅行の好みが貯まる、新しい旅のしおり</p>
          <h1 class="hero-title">
            <span class="hero-line">「どこ行く？」から</span>
            <span class="hero-line">「次どこ行く？」まで、</span>
            <span class="hero-line">みんなで</span>
          </h1>
          <p class="hero-subtitle">
            候補地選びから、計画、割り勘、そして旅の振り返りまで。
            グループ固有の好みが蓄積され、次の旅行がもっと楽しみになる共同編集アプリです。
          </p>
          <div class="hero-buttons">
            <router-link to="/trial" class="btn btn-secondary btn-lg">
              ログインせずに試す
            </router-link>
            <router-link to="/login" class="btn btn-primary btn-lg">
              無料で始める
            </router-link>
            <a href="#features" class="btn btn-secondary btn-lg">機能を見る</a>
          </div>
        </div>

        <div class="hero-visual" aria-label="RabiiTrip PC画面イメージ">
          <div class="pc-device-wrapper">
            <!-- PCディスプレイ本体 -->
            <div class="pc-device-display">
              <div class="pc-browser-topbar">
                <div class="pc-window-dots">
                  <span class="pc-dot dot-red"></span>
                  <span class="pc-dot dot-yellow"></span>
                  <span class="pc-dot dot-green"></span>
                </div>
                <div class="pc-address-pill">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="lock-icon"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                  <span>rabiitrip.com</span>
                </div>
                <div class="pc-topbar-spacer"></div>
              </div>
              <div class="pc-screen-viewport">
                <img
                  src="@/assets/hero-app-screen.png"
                  alt="RabiiTripのPC画面表示"
                  class="pc-screen-img"
                  fetchpriority="high"
                />
              </div>
            </div>
            <!-- PCベース（下部シャシー） -->
            <div class="pc-device-base">
              <div class="pc-device-notch"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="scene-section">
      <div class="container">
        <div class="section-heading">
          <p class="section-kicker">SCENES</p>
          <h2 class="section-title">旅のあらゆる場面で、しおりが役に立つ</h2>
          <p class="section-subtitle">
            計画中、移動中、帰ってからの振り返りまで。必要な情報を探し回らず、
            ひとつの場所で確認できます。
          </p>
        </div>

        <div
          ref="carouselRef"
          class="scene-carousel"
          role="region"
          aria-roledescription="カルーセル"
          aria-label="旅の利用シーン"
          tabindex="0"
          @keydown.left.prevent="showPrevious(true)"
          @keydown.right.prevent="showNext(true)"
          @mouseenter="isHovered = true"
          @mouseleave="isHovered = false"
          @focusin="isFocused = true"
          @focusout="handleFocusOut"
          @touchstart.passive="handleTouchStart"
          @touchend.passive="handleTouchEnd"
          @touchcancel.passive="resetTouch"
        >
          <div class="scene-viewport">
            <transition :name="isReducedMotion ? '' : 'scene-slide'" mode="out-in">
              <article
                :key="activeScene.title"
                class="scene-card scene-card-active"
                role="group"
                aria-roledescription="スライド"
                :aria-label="`${activeSceneIndex + 1} / ${travelScenes.length}`"
              >
                <img
                  :src="activeScene.image"
                  :alt="activeScene.alt"
                  class="scene-image"
                  width="1280"
                  height="853"
                  loading="eager"
                  decoding="async"
                />
                <div class="scene-body">
                  <span class="scene-label">{{ activeScene.label }}</span>
                  <h3>{{ activeScene.title }}</h3>
                  <p>{{ activeScene.description }}</p>
                </div>
              </article>
            </transition>
          </div>
          <button
            type="button"
            class="carousel-arrow carousel-arrow-prev"
            aria-label="前の利用シーン"
            @click="showPrevious(true)"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            class="carousel-arrow carousel-arrow-next"
            aria-label="次の利用シーン"
            @click="showNext(true)"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
          <div class="carousel-controls">
            <div class="carousel-dots" aria-label="利用シーンを選択">
              <button
                v-for="(scene, index) in travelScenes"
                :key="scene.title"
                type="button"
                class="carousel-dot"
                :class="{ active: activeSceneIndex === index }"
                :aria-label="`${index + 1}枚目を表示`"
                :aria-current="activeSceneIndex === index ? 'true' : undefined"
                @click="showScene(index, true)"
              ></button>
            </div>
            <button
              type="button"
              class="carousel-autoplay"
              :aria-label="autoplayControlLabel"
              @click="toggleAutoplay"
            >
              <svg v-if="isAutoplayRunning" aria-hidden="true" viewBox="0 0 24 24">
                <path d="M8 6v12M16 6v12" />
              </svg>
              <svg v-else aria-hidden="true" viewBox="0 0 24 24">
                <path d="m9 6 9 6-9 6Z" />
              </svg>
              <span>{{ isAutoplayRunning ? "停止" : "再生" }}</span>
            </button>
          </div>
          <p class="sr-only" aria-live="polite" aria-atomic="true">
            {{ carouselAnnouncement }}
          </p>
        </div>
      </div>
    </section>

    <section id="features" class="features-section">
      <div class="container">
        <div class="section-heading">
          <p class="section-kicker">FEATURES</p>
          <h2 class="section-title">みんなの好みが貯まる共同編集</h2>
          <p class="section-subtitle">
            行き先の検討から帰ってからのレビューまで。6つのステップをグループで共有できます。
          </p>
        </div>

        <div class="features-grid">
          <article
            v-for="feature in features"
            :key="feature.title"
            class="feature-card"
          >
            <div class="feature-icon">
              <IconSvg :name="feature.icon" size="lg" />
            </div>
            <h3 class="feature-title">{{ feature.title }}</h3>
            <p class="feature-description">{{ feature.description }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- New Section 1: 旅のサイクルセクション -->
    <section class="workflow-section">
      <div class="container">
        <div class="section-heading">
          <p class="section-kicker">HOW IT WORKS</p>
          <h2 class="section-title">旅のサイクル全6ステップをスムーズに</h2>
          <p class="section-subtitle">
            「旅の検討から、次の旅まで」
          </p>
        </div>
        <div class="workflow-grid staggered-reveal-container">
          <div v-for="(step, index) in workflowSteps" :key="step.title" class="workflow-step-card staggered-reveal-item" :style="{ transitionDelay: `${index * 0.1}s` }">
            <div class="step-header">
              <div class="step-number">{{ step.number }}</div>
              <div class="step-icon">
                <IconSvg :name="step.icon" size="lg" />
              </div>
            </div>
            <h3>{{ step.title }}</h3>
            <p class="step-subtitle">{{ step.subtitle }}</p>
            <p class="step-description">「{{ step.description }}」</p>
          </div>
        </div>
      </div>
    </section>

    <!-- New Section 2: 差別化訴求セクション -->
    <section class="differentiation-section">
      <div class="container">
        <div class="section-heading">
          <h2 class="section-title">このグループだけの旅の好みが、ここに育つ。</h2>
          <p class="section-subtitle">
            Googleマップや旅行レビューサイトは、不特定多数の評価。RabiiTripは、あなたのグループの評価。
          </p>
        </div>
        <div class="diff-grid staggered-reveal-container">
          <article class="diff-card staggered-reveal-item">
            <p>「このグループは自然体験の評価が高い」</p>
          </article>
          <article class="diff-card staggered-reveal-item" style="transition-delay: 0.1s">
            <p>「行列が長いレストランは評価が下がる傾向」</p>
          </article>
          <article class="diff-card staggered-reveal-item" style="transition-delay: 0.2s">
            <p>「観光を詰め込みすぎると満足度が下がる」</p>
          </article>
        </div>
        <div class="diff-summary">
          <p>だから、次の旅先選びがもっとスムーズになる。</p>
        </div>
      </div>
    </section>

    <section class="cta-section">
      <div class="container cta-layout">
        <div>
          <p class="section-kicker">START</p>
          <h2 class="cta-title">みんなの好みを蓄積して、次の旅行をもっと楽しく！</h2>
          <p class="cta-subtitle">
            無料で使える旅のしおりアプリ。まずは一つ、行きたい旅を作ってみてください。
          </p>
        </div>
        <router-link to="/trial" class="btn btn-secondary btn-lg">
          ログインせずに試す
        </router-link>
        <router-link to="/login" class="btn btn-primary btn-lg">
          無料で始める
        </router-link>
      </div>
    </section>

    <section id="faq" class="faq-section">
      <div class="container">
        <div class="section-heading">
          <p class="section-kicker">FAQ</p>
          <h2 class="section-title">よくある質問</h2>
        </div>
        <div class="faq-list">
          <div v-for="(faq, index) in faqs" :key="index" class="faq-item">
            <button class="faq-question" @click="toggleFaq(index)">
              <span>{{ faq.QUESTION_TEXT }}</span>
              <span class="faq-toggle">{{
                openFaqIndex === index ? "▲" : "▼"
              }}</span>
            </button>
            <div v-if="openFaqIndex === index" class="faq-answer">
              {{ faq.ANSWER_TEXT }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer class="landing-footer">
      <div class="container">
        <div class="footer-content">
          <div class="footer-brand">
            <div class="footer-logo">
              <img src="/favicon.png" alt="" class="footer-logo-image" />
              RabiiTrip
            </div>
            <p class="footer-tagline">「どこ行く？」から「次どこ行く？」まで、みんなで。</p>
          </div>
          <div class="footer-links">
            <ul>
              <li><router-link to="/trial">お試しページ</router-link></li>
              <li><router-link to="/login">ログイン</router-link></li>
              <li><a href="#features">機能</a></li>
              <li><a href="#faq">よくある質問</a></li>
            </ul>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; 2026 RabiiTrip. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: "LandingView",
};
</script>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import axios from "axios";
import IconSvg from "@/components/IconSvg.vue";
import { API_URL } from "@/config/api";

const heroPhoto = require("@/assets/landing/trip-planning-cafe.jpg");
const stationPhoto = require("@/assets/landing/trip-station-itinerary.jpg");
const expensePhoto = require("@/assets/landing/trip-evening-expenses.jpg");
const AUTOPLAY_INTERVAL_MS = 3000;

const travelScenes = [
  {
    label: "Planning",
    title: "みんなの行きたいところから旅先を決める",
    description:
      "候補地や集合時間をひとつのしおりにまとめて、みんなで確認しながら旅程を作れます。",
    image: heroPhoto,
    alt: "カフェで地図を囲みながら旅行計画を立てている様子",
  },
  {
    label: "On the go",
    title: "旅行中も全員でリアルタイム共有",
    description:
      "当日の集合、移動、観光、食事の流れをすぐ確認。共有リンクなら閲覧もかんたんです。",
    image: stationPhoto,
    alt: "駅のホームでスマートフォンの旅程を確認している旅行者",
  },
  {
    label: "After trip",
    title: "振り返りが、次の旅をもっとよくする",
    description:
      "思い出を振り返りながら費用を整理。帰宅後の面倒な精算も見通しよく進められます。",
    image: expensePhoto,
    alt: "旅行写真とレシートを見ながら費用を確認している様子",
  },
];

const features = [
  {
    icon: "calendar-solid-full",
    title: "スケジュール共同編集",
    description:
      "旅行スポット、集合時間、移動、費用を日程ごとに整理。旅の流れがひと目でわかります。",
  },
  {
    icon: "check-solid-full",
    title: "チェックリスト分担",
    description:
      "持ち物や買うものをメンバーで管理。準備の抜け漏れを減らして安心して出発できます。",
  },
  {
    icon: "yen-sign-solid-full",
    title: "割り勘精算",
    description:
      "誰が何を支払ったかを記録して、旅行後の精算をスムーズに。金額の確認も簡単です。",
  },
  {
    icon: "user-solid-full",
    title: "グループ共有",
    description:
      "家族や友達としおりを共有。パソコン、スマホ、タブレットから同じ情報を確認できます。",
  },
  {
    icon: "map-solid-full",
    title: "旅行先を選ぶ",
    description:
      "訪れた国や都道府県を地図で記録。旅の足跡を見ながら、次の目的地も探せます。",
  },
  {
    icon: "link-solid-full",
    title: "旅行後に評価する",
    description:
      "アカウント不要で閲覧できるリンクを発行。家族への共有や当日の案内にも便利です。",
  },
];

const workflowSteps = [
  {
    number: "01",
    title: "旅先検討",
    subtitle: "ウィッシュリスト投票・検討ボード",
    description: "全員が納得する旅先を決める",
    icon: "earth-americas-solid-full",
  },
  {
    number: "02",
    title: "旅行準備",
    subtitle: "チェックリスト（担当者・期限付き）",
    description: "幹事だけに集中しない",
    icon: "check-solid-full",
  },
  {
    number: "03",
    title: "旅程作成",
    subtitle: "しおり共同編集",
    description: "全員が提案・確認・変更できる",
    icon: "calendar-solid-full",
  },
  {
    number: "04",
    title: "旅行中",
    subtitle: "予定変更・支払い共有",
    description: "情報とお金の管理が散らばらない",
    icon: "map-solid-full",
  },
  {
    number: "05",
    title: "旅行後",
    subtitle: "アクティビティ・ホテル・食事・旅行全体を評価",
    description: "実際に良かったものを残せる",
    icon: "pen-to-square-solid-full",
  },
  {
    number: "06",
    title: "次の旅行",
    subtitle: "過去の評価からグループの好みを把握",
    description: "次の候補地をより決めやすくする",
    icon: "house-solid-full",
  },
];

const isMobileMenuOpen = ref(false);
const openFaqIndex = ref(null);
const faqs = ref([]);
const carouselRef = ref(null);
const activeSceneIndex = ref(0);
const isHovered = ref(false);
const isFocused = ref(false);
const isPageHidden = ref(false);
const isReducedMotion = ref(false);
const isAutoplayPausedByUser = ref(false);
const carouselAnnouncement = ref("");
const touchStartX = ref(null);
const touchStartY = ref(null);
let autoplayTimer = null;
let reducedMotionQuery = null;

const activeScene = computed(() => travelScenes[activeSceneIndex.value]);
const shouldAutoplay = computed(
  () =>
    !isHovered.value &&
    !isFocused.value &&
    !isPageHidden.value &&
    !isReducedMotion.value &&
    !isAutoplayPausedByUser.value
);
const isAutoplayRunning = computed(
  () => !isReducedMotion.value && !isAutoplayPausedByUser.value
);
const autoplayControlLabel = computed(() =>
  isAutoplayRunning.value ? "カルーセルの自動再生を停止" : "カルーセルを自動再生"
);

const normalizeSceneIndex = (index) =>
  ((index % travelScenes.length) + travelScenes.length) % travelScenes.length;

const showScene = (index, isManual = false) => {
  activeSceneIndex.value = normalizeSceneIndex(index);
  carouselAnnouncement.value = isManual
    ? `${activeSceneIndex.value + 1}枚目、${activeScene.value.title}`
    : "";
};

const showPrevious = (isManual = false) => {
  showScene(activeSceneIndex.value - 1, isManual);
};

const showNext = (isManual = false) => {
  showScene(activeSceneIndex.value + 1, isManual);
};

const toggleAutoplay = () => {
  if (isReducedMotion.value) {
    isAutoplayPausedByUser.value = false;
    return;
  }
  isAutoplayPausedByUser.value = !isAutoplayPausedByUser.value;
};

const stopAutoplay = () => {
  if (autoplayTimer !== null) {
    window.clearInterval(autoplayTimer);
    autoplayTimer = null;
  }
};

const startAutoplay = () => {
  stopAutoplay();
  if (shouldAutoplay.value) {
    autoplayTimer = window.setInterval(
      () => showNext(false),
      AUTOPLAY_INTERVAL_MS
    );
  }
};

const handleVisibilityChange = () => {
  isPageHidden.value = document.hidden;
};

const handleReducedMotionChange = (event) => {
  isReducedMotion.value = event.matches;
};

const handleFocusOut = (event) => {
  if (!carouselRef.value?.contains(event.relatedTarget)) {
    isFocused.value = false;
  }
};

const handleTouchStart = (event) => {
  touchStartX.value = event.changedTouches[0]?.clientX ?? null;
  touchStartY.value = event.changedTouches[0]?.clientY ?? null;
};

const resetTouch = () => {
  touchStartX.value = null;
  touchStartY.value = null;
};

const handleTouchEnd = (event) => {
  if (touchStartX.value === null || touchStartY.value === null) return;
  const touch = event.changedTouches[0];
  const deltaX = (touch?.clientX ?? touchStartX.value) - touchStartX.value;
  const deltaY = (touch?.clientY ?? touchStartY.value) - touchStartY.value;
  resetTouch();
  if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
  deltaX > 0 ? showPrevious(true) : showNext(true);
};

watch(shouldAutoplay, startAutoplay);

onMounted(async () => {
  isPageHidden.value = document.hidden;
  document.addEventListener("visibilitychange", handleVisibilityChange);
  reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  isReducedMotion.value = reducedMotionQuery.matches;
  reducedMotionQuery.addEventListener?.("change", handleReducedMotionChange);
  startAutoplay();

  try {
    const res = await axios.get(`${API_URL}/qa`);
    faqs.value = res.data;
  } catch (e) {
    console.error("FAQ取得エラー:", e);
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  document.querySelectorAll('.staggered-reveal-container').forEach(container => {
    observer.observe(container);
  });
});

onBeforeUnmount(() => {
  stopAutoplay();
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  reducedMotionQuery?.removeEventListener?.("change", handleReducedMotionChange);
});

const toggleFaq = (index) => {
  openFaqIndex.value = openFaqIndex.value === index ? null : index;
};
</script>

<style scoped>
.landing-page {
  background: var(--color-surface);
}

.landing-header {
  position: sticky;
  z-index: 50;
  top: 0;
  padding: 0.7rem 1.5rem;
  background: rgba(249, 252, 247, 0.92);
  border-bottom: 1px solid rgba(95, 127, 81, 0.16);
  box-shadow: 0 8px 30px rgba(31, 41, 55, 0.06);
  backdrop-filter: blur(16px);
}

.header-inner {
  width: min(1180px, 100%);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.header-brand,
.header-nav {
  display: flex;
  align-items: center;
}

.header-brand {
  gap: 0.55rem;
  color: var(--color-primary-ink);
  font-size: 1.1rem;
  font-weight: 900;
  text-decoration: none;
}

.header-logo {
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.header-nav {
  gap: clamp(0.65rem, 2vw, 1.4rem);
}

.header-nav a {
  color: var(--color-text);
  font-size: 0.9rem;
  font-weight: 800;
  text-decoration: none;
}

.header-nav a:hover,
.header-nav a:focus-visible {
  color: var(--color-primary-ink);
}

.header-nav .header-trial,
.header-nav .header-login {
  padding: 0.6rem 0.9rem;
  border: 1px solid var(--color-primary-ink);
  border-radius: 0.5rem;
}

.header-nav .header-login {
  color: white;
  background: var(--color-primary-ink);
}

#top,
#features,
#faq {
  scroll-margin-top: 5.5rem;
}

.hero-section {
  min-height: calc(100vh - 64px);
  padding: 2rem 1.5rem;
  background: #f6f7f3;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-content {
  width: min(1180px, 100%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(320px, 1.08fr);
  gap: clamp(2.5rem, 6vw, 5rem);
  align-items: center;
}

.hero-copy {
  position: relative;
  z-index: 2;
}

.hero-brand,
.section-kicker,
.scene-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 800;
  color: var(--color-primary-ink);
  letter-spacing: 0;
}

.hero-brand {
  margin-bottom: 1.25rem;
  font-size: 1.05rem;
}

.hero-logo {
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.hero-kicker {
  margin: 0 0 0.75rem;
  color: #48633c;
  font-weight: 700;
}

.hero-title {
  max-width: 100%;
  margin-bottom: 1.25rem;
  color: var(--color-text);
  font-size: clamp(1.85rem, 3.8vw, 3.25rem);
  font-weight: 900;
  line-height: 1.28;
  letter-spacing: -0.01em;
}

.hero-line {
  display: block;
  white-space: nowrap;
}

.hero-subtitle {
  max-width: 600px;
  margin-bottom: 2rem;
  color: var(--color-text);
  font-size: clamp(1rem, 1.8vw, 1.25rem);
  line-height: 1.9;
}

.hero-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.875rem;
  margin-bottom: 0;
}

/* ── PCデバイスモックアップ ── */
.hero-visual {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.pc-device-wrapper {
  width: 100%;
  max-width: 680px;
  filter: drop-shadow(0 25px 50px rgba(28, 41, 33, 0.16));
  transition: transform 0.4s ease;
}

.pc-device-wrapper:hover {
  transform: translateY(-4px);
}

.pc-device-display {
  background: #1e293b;
  border-radius: 12px 12px 2px 2px;
  padding: 10px 10px 0 10px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.15);
  position: relative;
}

.pc-browser-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px 8px;
  background: #0f172a;
  border-radius: 6px 6px 0 0;
  gap: 12px;
}

.pc-window-dots {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 50px;
}

.pc-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
}

.dot-red {
  background: #ff5f56;
}

.dot-yellow {
  background: #ffbd2e;
}

.dot-green {
  background: #27c93f;
}

.pc-address-pill {
  flex: 1;
  max-width: 260px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 2px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 0.6875rem;
  color: #94a3b8;
  letter-spacing: 0.02em;
}

.lock-icon {
  color: #64748b;
}

.pc-topbar-spacer {
  width: 50px;
}

.pc-screen-viewport {
  width: 100%;
  overflow: hidden;
  background: #f5f7f2;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
}

.pc-screen-img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
}

.pc-device-base {
  width: calc(100% + 24px);
  margin-left: -12px;
  height: 14px;
  background: linear-gradient(to bottom, #d1d5db, #9ca3af);
  border-radius: 0 0 10px 10px;
  position: relative;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.pc-device-notch {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 68px;
  height: 5px;
  background: #4b5563;
  border-radius: 0 0 4px 4px;
}

.scene-section,
.features-section,
.workflow-section,
.faq-section {
  padding: clamp(4rem, 8vw, 6.5rem) 1.5rem;
}

.scene-section {
  background: var(--color-surface);
}

.section-heading {
  max-width: 760px;
  margin: 0 auto 3rem;
  text-align: center;
}

.section-kicker {
  margin-bottom: 0.75rem;
  font-size: 0.78rem;
  text-transform: uppercase;
}

.section-title {
  margin-bottom: 1rem;
  color: var(--color-text);
  font-size: clamp(1.8rem, 4vw, 3rem);
  font-weight: 900;
}

.section-subtitle {
  margin: 0 auto;
  color: #5b6472;
  font-size: 1rem;
  line-height: 1.9;
}

.scene-carousel {
  position: relative;
  max-width: 980px;
  margin: 0 auto;
  padding: 0 4rem 2.8rem;
  outline: none;
}

.scene-carousel:focus-visible {
  border-radius: 0.5rem;
  box-shadow: 0 0 0 4px rgba(111, 150, 93, 0.28);
}

.scene-viewport {
  overflow: hidden;
  border-radius: 0.5rem;
}

.scene-card,
.feature-card {
  overflow: hidden;
  background: var(--color-surface);
  border: 1px solid #e7eee4;
  border-radius: 0.5rem;
  box-shadow: 0 10px 30px rgba(31, 41, 55, 0.08);
}

.scene-card-active {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(280px, 0.65fr);
  min-height: 430px;
}

.scene-card-active .scene-image {
  height: 100%;
  aspect-ratio: auto;
}

.scene-card-active .scene-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(1.5rem, 4vw, 3rem);
}

.scene-card-active .scene-body h3 {
  font-size: clamp(1.35rem, 3vw, 2.2rem);
  line-height: 1.35;
}

.carousel-arrow {
  position: absolute;
  z-index: 2;
  top: calc(50% - 1.4rem);
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  padding: 0;
  color: white;
  background: #36512d;
  border: 0;
  border-radius: 50%;
  box-shadow: 0 10px 25px rgba(31, 41, 55, 0.2);
  cursor: pointer;
}

.carousel-arrow svg {
  width: 24px;
  height: 24px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.carousel-arrow-prev { left: 0; }
.carousel-arrow-next { right: 0; }

.carousel-arrow:hover,
.carousel-arrow:focus-visible {
  background: var(--color-primary-ink);
  transform: scale(1.06);
}

.carousel-controls {
  position: absolute;
  bottom: 0.45rem;
  left: 50%;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  transform: translateX(-50%);
}

.carousel-dots {
  display: flex;
}

.carousel-dot {
  position: relative;
  width: 44px;
  height: 44px;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.carousel-dot::before {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 12px;
  content: "";
  background: #c7d4c1;
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.carousel-dot.active::before {
  width: 30px;
  background: var(--color-primary-ink);
  border-radius: 999px;
}

.carousel-autoplay {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  min-width: 70px;
  min-height: 44px;
  padding: 0.35rem 0.6rem;
  color: #36512d;
  background: var(--color-surface);
  border: 1px solid #a9bea0;
  border-radius: 999px;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;
}

.carousel-autoplay svg {
  width: 17px;
  height: 17px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.carousel-autoplay:hover,
.carousel-autoplay:focus-visible {
  color: white;
  background: var(--color-primary-ink);
}

.scene-slide-enter-active,
.scene-slide-leave-active {
  transition: opacity 260ms ease, transform 260ms ease;
}

.scene-slide-enter-from {
  opacity: 0;
  transform: translateX(28px);
}

.scene-slide-leave-to {
  opacity: 0;
  transform: translateX(-28px);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.scene-image {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
}

.scene-body {
  padding: 1.35rem;
}

.scene-label {
  margin-bottom: 0.65rem;
  font-size: 0.74rem;
}

.scene-body h3,
.step-row h3 {
  margin-bottom: 0.5rem;
  color: var(--color-text);
  font-size: 1.12rem;
}

.scene-body p,
.step-row p {
  margin: 0;
  color: #5b6472;
  font-size: 0.94rem;
  line-height: 1.75;
}

.features-section {
  background: #f7faf5;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.feature-card {
  padding: 1.5rem;
  transition: transform var(--transition-base),
    box-shadow var(--transition-base), border-color var(--transition-base);
}

.feature-card:hover {
  transform: translateY(-4px);
  border-color: #b4cf9e;
  box-shadow: 0 18px 42px rgba(83, 106, 72, 0.12);
}

.feature-icon {
  display: inline-grid;
  place-items: center;
  width: 54px;
  height: 54px;
  margin-bottom: 1rem;
  color: var(--color-primary-ink);
  background: #eef7e9;
  border-radius: 0.5rem;
}

.feature-title {
  margin-bottom: 0.75rem;
  color: var(--color-text);
  font-size: 1.18rem;
}

.feature-description {
  margin: 0;
  color: #5b6472;
  font-size: 0.94rem;
  line-height: 1.75;
}

.workflow-section {
  background: var(--color-surface);
  padding: 4rem 1.5rem;
}
.workflow-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 3rem;
}
.workflow-step-card {
  background: #fcfcfc;
  border: 1px solid #eaeaea;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
}
.step-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.step-number {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  color: white;
  background: #6f965d;
  border-radius: 50%;
  font-weight: 900;
  font-size: 0.9rem;
}
.step-icon {
  color: var(--color-primary-ink);
}
.step-subtitle {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.5rem;
  font-weight: 600;
}
.step-description {
  font-size: 0.95rem;
  color: #333;
}
.differentiation-section {
  padding: 4rem 1.5rem;
  background: #f4f7f4;
}
.diff-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;
}
.diff-card {
  background: var(--color-surface);
  border-left: 4px solid #5f7f51;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  font-size: 1rem;
  font-weight: 600;
}
.diff-summary {
  text-align: center;
  margin-top: 3rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--color-primary-ink);
}
.staggered-reveal-item {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s cubic-bezier(0.2, 0.8, 0.2, 1), transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.staggered-reveal-container.is-visible .staggered-reveal-item {
  opacity: 1;
  transform: translateY(0);
}

.cta-section {
  padding: clamp(3.5rem, 7vw, 5.5rem) 1.5rem;
  background: var(--color-surface);
}

.cta-layout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding-block: clamp(2rem, 5vw, 3rem);
  border-top: 1px solid #e7eee4;
  border-bottom: 1px solid #e7eee4;
}

.cta-title {
  max-width: 760px;
  margin-bottom: 0.75rem;
  color: var(--color-text);
  font-size: clamp(1.7rem, 4vw, 2.8rem);
}

.cta-subtitle {
  max-width: 680px;
  margin: 0;
  color: #5b6472;
}

.faq-section {
  background: #f7faf5;
}

.faq-list {
  max-width: 860px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.faq-item {
  background: var(--color-surface);
  border: 1px solid #e7eee4;
  border-radius: 0.5rem;
  box-shadow: 0 8px 24px rgba(31, 41, 55, 0.06);
  overflow: hidden;
}

.faq-question {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text);
  font-family: inherit;
  font-size: 1rem;
  font-weight: 800;
  text-align: left;
}

.faq-question:hover {
  background: #fbfdf9;
}

.faq-toggle {
  flex-shrink: 0;
  color: #6f965d;
  font-size: 0.75rem;
}

.faq-answer {
  padding: 0 1.5rem 1.25rem;
  color: #5b6472;
  font-size: 0.95rem;
  line-height: 1.8;
}

.landing-footer {
  padding: 4rem 1.5rem 2rem;
  color: white;
  background: #1f2937;
}

.footer-content {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2rem;
  margin-bottom: 2rem;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
  font-weight: 800;
}

.footer-logo-image {
  width: 30px;
  height: 30px;
  object-fit: contain;
}

.footer-tagline,
.footer-bottom p {
  margin: 0;
  color: rgba(255, 255, 255, 0.68);
}

.footer-links ul {
  display: flex;
  gap: 1.25rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.footer-links a {
  color: rgba(255, 255, 255, 0.72);
  text-decoration: none;
  font-weight: 700;
}

.footer-links a:hover {
  color: white;
}

.footer-bottom {
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  text-align: center;
}

@media (max-width: 1024px) {
  .hero-content,
  .workflow-layout {
    grid-template-columns: 1fr;
  }

  .hero-stats {
    margin-top: 0;
  }

  .hero-title {
    max-width: 12em;
  }

  .hero-visual {
    min-height: auto;
  }

  .hero-photo {
    height: auto;
    min-height: 360px;
  }

  .features-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .hero-section,
  .scene-section,
  .features-section,
  .workflow-section,
  .faq-section,
  .cta-section,
  .landing-footer {
    padding-inline: 1rem;
  }

  .hero-stats,
  .features-grid {
    grid-template-columns: 1fr;
  }

  .hero-buttons,
  .cta-layout {
    align-items: stretch;
    flex-direction: column;
  }

  .hero-buttons .btn,
  .cta-layout .btn {
    width: 100%;
  }

  .hero-photo {
    min-height: 300px;
  }

  .schedule-panel {
    position: relative;
    left: auto;
    bottom: auto;
    width: 100%;
    margin-top: 1rem;
  }

  .members-panel {
    top: 0.75rem;
    right: 0.75rem;
  }

  .workflow-grid, .diff-grid {
    grid-template-columns: 1fr;
  }

  .footer-content,
  .footer-links ul {
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .mobile-menu-toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-text);
  }

  .mobile-menu-toggle-btn svg {
    width: 24px;
    height: 24px;
  }

  .desktop-nav {
    display: none !important;
  }

  .header-inner {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }

  .mobile-landing-nav {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
    background: var(--color-surface);
    border-top: 1px solid var(--color-border);
    margin-top: 0.5rem;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-md);
  }

  .mobile-landing-nav a {
    padding: 0.75rem 1rem;
    border-radius: var(--radius-md);
    text-decoration: none;
    font-weight: 700;
    color: var(--color-text);
    text-align: center;
  }

  .hero-buttons {
    flex-direction: column;
    width: 100%;
    gap: 0.75rem;
  }

  .hero-buttons .btn {
    width: 100%;
    justify-content: center;
  }

  .scene-carousel {
    padding-inline: 0;
  }

  .scene-card-active {
    grid-template-columns: 1fr;
    min-height: 0;
  }

  .scene-card-active .scene-image {
    height: auto;
    aspect-ratio: 4 / 3;
  }

  .carousel-arrow {
    top: 28%;
    width: 44px;
    height: 44px;
  }

  .carousel-arrow-prev { left: 0.65rem; }
  .carousel-arrow-next { right: 0.65rem; }
}

.mobile-menu-toggle-btn {
  display: none;
}

@media (prefers-reduced-motion: reduce) {
  .scene-slide-enter-active,
  .scene-slide-leave-active,
  .carousel-arrow,
  .carousel-dot {
    transition: none;
  }
}
</style>
